import type { NavigateFn } from "@tanstack/react-router";
import { useNavigate, useRouterState } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import type { HydratedImage, ParameterGroup } from "../generated/api/types.gen";
import { runInitialRegenerate } from "../lib/results-navigation";
import { useImageStreaming } from "./useImageStreaming";

interface UseResultsPageParams {
  sessionId: string;
  requestId: string;
  originalPrompt: string | undefined;
  projectId: string | undefined;
}

function useResultsForm(originalPrompt: string | undefined) {
  const {
    reset,
    control,
    formState: { isDirty },
  } = useForm<{
    parameter_groups: ParameterGroup[];
    prompt: string;
  }>({ defaultValues: { parameter_groups: [] }, mode: "onChange" });
  const prompt = useWatch({ control, name: "prompt" }) ?? "";
  useEffect(() => {
    reset({ prompt: originalPrompt || "" });
  }, [originalPrompt, reset]);
  return { reset, control, isDirty, prompt };
}

function useRegeneratingEffect(
  isRegenerating: boolean,
  images: HydratedImage[] | undefined,
  isInitialLoading: boolean,
  setIsRegenerating: (v: boolean) => void,
) {
  useEffect(() => {
    if (!isRegenerating) return;
    if (!images || images.length === 0 || isInitialLoading || images.length < 4)
      return;
    const timer = setTimeout(() => {
      if (images.length >= 4 && !isInitialLoading) setIsRegenerating(false);
    }, 300);
    return () => clearTimeout(timer);
  }, [
    images,
    images?.length,
    isRegenerating,
    isInitialLoading,
    setIsRegenerating,
  ]);
}

interface UseResultsStreamingParams {
  requestId: string;
  sessionId: string;
  prompt: string;
  projectId: string | undefined;
  uploadedFiles: (File | null)[] | null | undefined;
  referenceAssetIds?: (string | null)[] | null;
}

function useResultsStreaming({
  requestId,
  sessionId,
  prompt,
  projectId,
  uploadedFiles,
  referenceAssetIds,
}: UseResultsStreamingParams) {
  const [isRegenerating, setIsRegenerating] = useState(false);
  const {
    images,
    isInitialLoading,
    error: generationError,
  } = useImageStreaming({
    requestId,
    sessionId,
    prompt,
    imageFiles: uploadedFiles ?? null,
    referenceAssetIds,
    projectId,
  });
  useRegeneratingEffect(
    isRegenerating,
    images,
    isInitialLoading,
    setIsRegenerating,
  );
  return {
    images,
    isInitialLoading,
    generationError,
    isRegenerating,
    setIsRegenerating,
  };
}

interface ResultsPageHandlerDeps {
  params: UseResultsPageParams;
  navigate: NavigateFn;
  uploadedFiles: (File | null)[] | null | undefined;
  referenceImagePreviews: string[] | null | undefined;
  referenceAssetIds?: (string | null)[] | null;
  prompt: string;
  images: HydratedImage[] | undefined;
  setSelectedImageIndex: (i: number | null) => void;
  setIsRegenerating: (v: boolean) => void;
}

function useResultsPageHandlers({
  params,
  navigate,
  uploadedFiles,
  referenceImagePreviews,
  referenceAssetIds,
  prompt,
  images,
  setSelectedImageIndex,
  setIsRegenerating,
}: ResultsPageHandlerDeps) {
  const { sessionId, requestId } = params;
  return {
    handleInitialRegenerate: () =>
      runInitialRegenerate({
        prompt,
        sessionId,
        uploadedFiles,
        referenceImagePreviews,
        referenceAssetIds,
        navigate,
        setSelectedImageIndex,
        setIsRegenerating,
      }),
    handleEnterRefinementMode: (index: number) => {
      const image = images?.[index];
      if (!image) return;
      setSelectedImageIndex(index);
      navigate({
        to: "/refine",
        search: {
          requestId,
          imageId: image.id,
          sessionId,
        },
      });
    },
  };
}

export type ResultsPageState = ReturnType<typeof useResultsPage>;

export function useResultsPage(params: UseResultsPageParams) {
  const { sessionId, requestId, originalPrompt, projectId } = params;
  const navigate = useNavigate();
  const { uploadedFiles, referenceImagePreviews, referenceAssetIds } =
    useRouterState({
      select: (s) => s.location.state,
    });
  const { control, isDirty, prompt } = useResultsForm(originalPrompt);
  const [selectedImageIndex, setSelectedImageIndex] = useState<number | null>(
    null,
  );
  const [isExpandModalOpen, setIsExpandModalOpen] = useState(false);
  const streaming = useResultsStreaming({
    requestId,
    sessionId,
    prompt,
    projectId,
    uploadedFiles,
    referenceAssetIds,
  });
  const {
    images,
    isInitialLoading,
    generationError,
    isRegenerating,
    setIsRegenerating,
  } = streaming;
  const { handleInitialRegenerate, handleEnterRefinementMode } =
    useResultsPageHandlers({
      params,
      navigate,
      uploadedFiles,
      referenceImagePreviews,
      referenceAssetIds,
      prompt,
      images,
      setSelectedImageIndex,
      setIsRegenerating,
    });

  return {
    images,
    isInitialLoading,
    generationError,
    isDirty,
    control,
    selectedImageIndex,
    setSelectedImageIndex,
    isRegenerating,
    isExpandModalOpen,
    setIsExpandModalOpen,
    uploadedFiles,
    referenceImagePreviews,
    handleInitialRegenerate,
    handleEnterRefinementMode,
  };
}
